"use client";

import { useCallback, useState, useMemo, useRef } from "react";
import {
  DataGridPro,
  DataGridProProps,
  GridEventListener,
  useGridApiRef,
  GridRowSelectionModel,
  GridRowId,
  GridPaginationModel,
} from "@mui/x-data-grid-pro";
import BookmarkActions from "./BookmarkActions/bookmarkActions";
import { Stack } from "@mui/material";
import { BookmarkV2Response } from "@/api/bookmarks";
import { useRouter, useSearchParams } from "next/navigation";
import BookmarkDisplay from "./bookmarkDisplay";
import { DEFAULT_PER_PAGE, PER_PAGE_OPTIONS } from "@/utils/tableUtils";

export default function BookmarkDataGridPro({
  bookmarkData,
  dataGridProps,
  total,
}: {
  bookmarkData: Record<string, string>;
  dataGridProps: DataGridProProps<BookmarkV2Response>;
  total: number;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const apiRef = useGridApiRef();
  const rowCountRef = useRef(total || 0);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });

  const page = parseInt(searchParams?.get('page') || '0', 10);
  const pageSize = parseInt(searchParams?.get('page_size') || DEFAULT_PER_PAGE.toString(), 10);
  
  const paginationModel: GridPaginationModel = useMemo(() => ({
    page,
    pageSize,
  }), [page, pageSize]);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    const params = new URLSearchParams(searchParams?.toString() || '');
    params.set('page', model.page.toString());
    params.set('page_size', model.pageSize.toString());
    router.push(`?${params.toString()}`);
  }, [router, searchParams]);



  const selectedRowModels = useMemo(() => {
    if (!apiRef.current) return [];
    const bookmarkMap = apiRef.current.getRowModels()
    const selectedBookmarks: BookmarkV2Response[] = [];
    rowSelectionModel.ids.forEach((id) => selectedBookmarks.push(bookmarkMap.get(id) as BookmarkV2Response));
    return selectedBookmarks;
  }, [rowSelectionModel?.ids, apiRef]);

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridProProps['getDetailPanelContent']>
  >(({ row }) => {
  return <BookmarkDisplay row={row} backgroundColor={bookmarkData[String(row.id)]} />;
}, [bookmarkData]);

  const onRowClick = useCallback<GridEventListener<"rowClick">>(
    (params) => {
      apiRef.current?.toggleDetailPanel(params.id);
    },
    [apiRef],
  );

  const onActionSuccess = useCallback(() => {
    setRowSelectionModel({
      type: 'include',
      ids: new Set<GridRowId>(),
    });
  }, []);

  return (
    <>
      <DataGridPro<BookmarkV2Response>
        apiRef={apiRef}
        {...dataGridProps}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        getDetailPanelHeight={useCallback(() => "auto", [])}
        getDetailPanelContent={getDetailPanelContent}
        disableColumnFilter
        disableColumnMenu
        disableColumnSorting
        rowCount={rowCountRef.current}
        onRowClick={onRowClick}
        paginationMode="server"
        pageSizeOptions={PER_PAGE_OPTIONS}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        getRowId={(row) => row.id}
        showToolbar
        sx={{
          ...dataGridProps.sx,
        }}
        slots={{
          noRowsOverlay: () => (
            <Stack className="h-full items-center justify-center text-xl">
              No Folders Found
            </Stack>
          ),
          noResultsOverlay: () => (
            <Stack className="h-full items-center justify-center text-xl">
              Filters Returned No Documents
            </Stack>
          ),
          toolbar: () => (
            <BookmarkActions
              selectedRows={selectedRowModels}
              onActionSuccess={onActionSuccess}
            />
          ),
        }}
      />
    </>
  );
}
